<template>
  <!-- Tenant Search Dialog -->
  <div v-if="showTenantSearchDialog" class="edit-dialog-overlay">
    <div class="tenant-search-dialog">
      <div ref="personDialog" class="edit-dialog">
        <div class="edit-dialog-content">
          <div
            class="dialog-title-bar draggable-header"
            title="Drag to move"
          >
            <h2>{{ isEditing ? 'Edit Person' : 'Add New Person' }}</h2>
            <button class="close-x-btn" @click="closeTenantSearchDialog">
              &times;
            </button>
          </div>
          <div class="edit-dialog-body">
            <div v-if="!showNewPersonDialog">
              <input
                type="text"
                v-model="tenantSearchQuery"
                @input="debouncedTenantSearch"
                placeholder="Search existing people ..."
                class="search-input"
              />
              <span style="margin-left: 20px"
                >(double click below to edit) </span
              ><i class="fas fa-down-long"></i>
            </div>
            <!-- Toggle between search results and add new person form -->
            <div v-if="showNewPersonDialog">
              <div class="scrollbar-wrapper">
                <div class="add-new-person">
                  <form @submit.prevent="submitForm">
                    <!-- Left Column -->
                    <div class="form-row">
                      <div class="form-column half-width left-column">
                        <div class="form-row">
                          <div class="form-group half-width">
                            <label for="firstName">First Name</label>
                            <input
                              type="text"
                              ref="firstName"
                              id="firstName"
                              v-model="firstName"
                              required
                            />
                          </div>
                          <div class="form-group half-width">
                            <label for="lastName">Last Name</label>
                            <input
                              type="text"
                              id="lastName"
                              v-model="lastName"
                              required
                            />
                          </div>
                        </div>
                        <div class="form-group">
                          <label for="fullName">Full Name</label>
                          <input
                            type="text"
                            id="fullName"
                            v-model="fullName"
                            required
                          />
                        </div>
                        <div class="form-group">
                          <label for="phones">Phone(s)</label>
                          <textarea
                            id="phones"
                            v-model="phoneInput"
                            placeholder="Enter phones separated by commas"
                          ></textarea>
                        </div>
                        <div class="form-group">
                          <label>Email(s)</label>
                          <div class="email-rows">
                            <div class="email-row email-row-head">
                              <span class="email-flag">A</span>
                              <span class="email-flag">P</span>
                            </div>
                            <div
                              v-for="(row, index) in emailRows"
                              :key="index"
                              class="email-row"
                            >
                              <input
                                type="checkbox"
                                :checked="!!row.isActive"
                                title="Active"
                                @change="
                                  emailRows = toggleActive(emailRows, index)
                                "
                              />
                              <input
                                type="checkbox"
                                :checked="isPrimaryTenant && !!row.isActive"
                                :disabled="
                                  !canCheckPrimary({
                                    isActive: row.isActive,
                                    primaryBlocked,
                                    leaseCurrent,
                                  })
                                "
                                :title="primaryCheckboxTitle(row)"
                                @change="
                                  isPrimaryTenant = $event.target.checked
                                "
                              />
                              <input
                                type="text"
                                v-model="row.contactValue"
                                placeholder="someone@somewhere.com"
                              />
                              <button
                                type="button"
                                class="email-trash"
                                title="Delete"
                                @click="
                                  emailRows = rowAfterDelete(emailRows, index)
                                "
                              >
                                <i class="fas fa-trash"></i>
                              </button>
                            </div>
                            <div class="email-row email-row-add">
                              <button
                                type="button"
                                title="Add email"
                                @click="emailRows = addEmailRow(emailRows)"
                              >
                                <i class="fas fa-plus"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <!-- Right Column -->
                      <div class="form-column half-width">
                        <div class="form-row">
                          <div class="form-group half-width">
                            <label for="activeFrom">Active From</label>
                            <input
                              type="date"
                              id="activeFrom"
                              v-model="activeFrom"
                            />
                          </div>
                          <div class="form-group half-width">
                            <label for="deactivatedOn">Deactivated On</label>
                            <input
                              type="date"
                              id="deactivatedOn"
                              v-model="deactivatedOn"
                            />
                          </div>
                        </div>
                        <div class="form-group">
                          <label for="forwardingNote">Forwarding Note</label>
                          <textarea
                            id="forwardingNote"
                            v-model="forwardingNote"
                          ></textarea>
                        </div>
                        <div class="form-group">
                          <label for="flags">Flags</label>
                          <input type="text" id="flags" v-model="flags" />
                        </div>
                      </div>
                    </div>
                    <!-- Checkboxes row (full width) -->
                    <div class="form-row checkbox-row">
                      <div class="form-group checkbox-item">
                        <label for="isTenant">Is Tenant</label>
                        <input
                          type="checkbox"
                          id="isTenant"
                          v-model="tenantCheckboxValue"
                        />
                      </div>
                      <div class="form-group checkbox-item">
                        <label for="isContractor">Contractor</label>
                        <input
                          type="checkbox"
                          id="isContractor"
                          v-model="contractorCheckboxValue"
                        />
                      </div>
                      <div class="form-group checkbox-item">
                        <label for="active">Active</label>
                        <input
                          type="checkbox"
                          id="active"
                          v-model="activeCheckboxValue"
                        />
                      </div>
                    </div>
                    <!-- Quick Sign-In Code (same UI for add + edit; actions need a saved person id) -->
                    <div class="form-group quick-code-section">
                      <label>Quick Sign-In Code</label>
                      <div class="quick-code-row">
                        <div class="quick-code-boxes">
                          <span
                            v-for="(ch, idx) in quickSignInCodeChars"
                            :key="idx"
                            class="quick-code-box"
                            :class="{ filled: ch !== '' }"
                            >{{ ch }}</span
                          >
                        </div>
                        <button
                          type="button"
                          class="generate-code-btn"
                          :disabled="!canManageQuickSignIn"
                          :title="
                            canManageQuickSignIn
                              ? 'Generate a new quick sign-in code'
                              : 'Save the person first, then generate a code'
                          "
                          @click="generateNewCode"
                        >
                          Generate New
                        </button>
                        <button
                          type="button"
                          class="blacklist-token-btn"
                          :disabled="!canManageQuickSignIn"
                          :title="
                            canManageQuickSignIn
                              ? 'Revoke PixieChatBot login tokens so this account cannot stay logged in'
                              : 'Save the person first to manage login tokens'
                          "
                          @click="blacklistTenantTokens"
                        >
                          Blacklist
                        </button>
                      </div>
                      <div v-if="!canManageQuickSignIn" class="quick-code-hint">
                        Save person first to generate a Quick Sign-In code and
                        manage ChatBot tokens.
                      </div>
                    </div>
                    <div
                      v-if="isEditing && selectedPerson && selectedPerson.id"
                      class="form-group sms-panel"
                    >
                      <label>SMS</label>
                      <div class="sms-status">
                        {{ smsStatus.smsOptIn ? 'Opted in' : 'Opted out' }}
                        <span v-if="smsStatus.smsPhoneDigits">
                          at {{ formatSmsPhone(smsStatus.smsPhoneDigits) }}
                        </span>
                      </div>
                      <label class="sms-confirm">
                        <input v-model="smsConfirmed" type="checkbox" />
                        <span>
                          I confirm this person agreed to receive SMS from Big
                          Property Management at this number about rent, work
                          orders, and account notices. Reply STOP to opt out.
                          Msg &amp; data rates may apply.
                        </span>
                      </label>
                      <div class="sms-actions">
                        <button
                          type="button"
                          class="btn-small"
                          :disabled="smsBusy || !smsConfirmed"
                          @click="setSmsOptIn(true)"
                        >
                          Opt in
                        </button>
                        <button
                          type="button"
                          class="btn-small"
                          :disabled="smsBusy"
                          @click="setSmsOptIn(false)"
                        >
                          Opt out
                        </button>
                        <button
                          type="button"
                          class="btn-small"
                          :disabled="smsBusy || !smsStatus.smsOptIn"
                          @click="sendTestSms"
                        >
                          Send test SMS
                        </button>
                      </div>
                      <div v-if="smsMessage" class="sms-note">{{ smsMessage }}</div>
                      <div class="sms-history-toolbar">
                        <input
                          v-model="smsHistoryQuery"
                          type="text"
                          class="sms-history-filter"
                          placeholder="Filter messages"
                        />
                        <button
                          type="button"
                          class="btn-small sms-history-refresh"
                          @click="loadSms"
                        >
                          Refresh
                        </button>
                      </div>
                      <div class="sms-history-scroll">
                      <table class="sms-history">
                        <thead>
                          <tr>
                            <th>When</th>
                            <th>Dir</th>
                            <th>Status</th>
                            <th>Message</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-if="!filteredSmsHistory.length">
                            <td colspan="4">
                              {{
                                smsHistory.length
                                  ? 'No matching messages'
                                  : 'No messages'
                              }}
                            </td>
                          </tr>
                          <tr
                            v-for="row in filteredSmsHistory"
                            :key="row.id"
                            class="sms-history-row"
                            @click="openSmsDetail(row)"
                          >
                            <td>{{ formatSmsStamp(row.createdAt) }}</td>
                            <td>{{ row.direction }}</td>
                            <td>{{ row.status || '' }}</td>
                            <td>{{ smsHistoryLine(row) }}</td>
                          </tr>
                        </tbody>
                      </table>
                      </div>
                    </div>
                    <div
                      v-if="smsDetail"
                      class="sms-detail-overlay"
                      @click.self="closeSmsDetail"
                    >
                      <div
                        class="sms-detail-popup"
                        role="dialog"
                        aria-label="SMS message"
                      >
                        <button
                          type="button"
                          class="sms-detail-close"
                          aria-label="Close"
                          @click="closeSmsDetail"
                        >
                          &times;
                        </button>
                        <h3>SMS message</h3>
                        <dl class="sms-detail-fields">
                          <dt>When</dt>
                          <dd>{{ formatSmsStamp(smsDetail.createdAt) }}</dd>
                          <dt>Direction</dt>
                          <dd>{{ smsDetail.direction }}</dd>
                          <dt>Phone</dt>
                          <dd>{{ formatSmsPhone(smsDetail.phoneDigits) }}</dd>
                          <dt>Status</dt>
                          <dd>{{ smsDetail.status || '' }}</dd>
                          <dt>Template</dt>
                          <dd>{{ smsDetail.templateKey || '' }}</dd>
                          <dt>Twilio id</dt>
                          <dd>{{ smsDetail.twilioSid || '' }}</dd>
                        </dl>
                        <pre v-if="smsDetail.body" class="sms-detail-body">{{
                          smsDetail.body
                        }}</pre>
                        <template
                          v-for="item in smsDetail.media || []"
                          :key="item.id"
                        >
                          <img
                            v-if="smsMediaUrls[item.id]"
                            class="sms-detail-media"
                            :src="smsMediaUrls[item.id]"
                            alt="Photo"
                          />
                        </template>
                      </div>
                    </div>
                    <!-- Buttons Row spanning both columns -->
                    <div class="error-msg">{{ newPersonErrMsg }}</div>
                    <div class="form-row button-row">
                      <button
                        type="submit"
                        :disabled="newPersonErrMsg !== ''"
                        class="half-width"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        @click="cancelAddPerson"
                        class="half-width"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <!-- Display search results or a message if no results -->
            <div v-else>
              <div v-if="tenantSearchResults.length" class="search-results">
                <ul>
                  <li
                    v-for="person in tenantSearchResults"
                    :key="person.id + '-' + person.leaseId"
                    @click="handleClick(person)"
                    @dblclick="handleDblClick(person)"
                    :class="{
                      'selected-item':
                        selectedPerson && selectedPerson.id === person.id,
                    }"
                  >
                    <span
                      v-html="
                        highlightTerm(
                          this.tenantSearchQuery,
                          person.fullName + ' ',
                        )
                      "
                    ></span>
                    <span
                      v-html="
                        highlightTerm(
                          this.tenantSearchQuery,
                          person.phone ? person.phone + ' ' : ' ',
                        )
                      "
                    ></span>
                    <span
                      v-html="
                        highlightTerm(
                          this.tenantSearchQuery,
                          person.email ? person.email + ' ' : ' ',
                        )
                      "
                    ></span>
                    <span>{{ person.active === 1 ? '(active)' : '' }}</span>
                  </li>
                </ul>
              </div>
              <div v-else class="search-results">No results found.</div>
            </div>
          </div>
          <div class="edit-dialog-footer">
            <button v-if="!showNewPersonDialog" @click="toggleNewPersonDialog">
              New Contact
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig'; // Adjust based on your axios configuration
import { filterSmsHistory, smsHistoryLine } from '@/utils/personSmsHistory';
import {
  loadSmsMediaObjectUrls,
  revokeSmsMediaUrls,
} from '@/utils/smsMedia';
import {
  toggleActive,
  canCheckPrimary,
  rowAfterDelete,
  addEmailRow,
} from '@/utils/personEmailRows';

// Get today's date
const today = new Date();

// Format it to YYYY-MM-DD
const year = today.getFullYear();
const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are zero-based
const day = String(today.getDate()).padStart(2, '0');

// Combine into the desired format
const formattedToday = `${year}-${month}-${day}`;

export default {
  props: {
    initialTenantQuery: {
      type: String,
      default: '',
    },
    /** When set, open directly in edit mode for this person (e.g. from LeaseSearch). */
    personToEdit: {
      type: Object,
      default: null,
    },
    /** When true, open directly on the add-person form. */
    startInAddMode: {
      type: Boolean,
      default: false,
    },
    leaseId: {
      type: Number,
      default: null,
    },
    leaseCurrent: {
      type: Boolean,
      default: false,
    },
    primaryBlocked: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Person form data
      firstName: '',
      lastName: '',
      fullName: '',
      phoneInput: '',
      emailRows: [],
      isPrimaryTenant: false,
      primaryBlocked: false,
      leaseCurrent: false,
      blockedBecause: null,
      primaryLeaseId: null,
      newPersonErrMsg: '',
      isTenant: false,
      isContractor: false,
      active: false,
      activeFrom: formattedToday,
      deactivatedOn: null,
      forwardingNote: '',
      flags: '',
      quickSignInCode: '',

      // State management for form
      isEditing: false,
      selectedPerson: null,
      showNewPersonDialog: false,
      showTenantSearchDialog: true,

      // Search results for tenant selection
      tenantSearchResults: [],
      tenantSearchQuery: this.initialTenantQuery,

      // Timeout to differentiate between single and double clicks
      clickTimeout: null,
      smsStatus: {
        smsOptIn: false,
        smsPhoneDigits: null,
      },
      smsHistory: [],
      smsMediaUrls: {},
      smsHistoryQuery: '',
      smsDetail: null,
      smsConfirmed: false,
      smsBusy: false,
      smsMessage: "",
    };
  },

  async mounted() {
    window.addEventListener('keydown', this.onSmsDetailKeydown);
    this.initializeFromRoute();

    try {
      if (this.personToEdit && this.personToEdit.id) {
        this.editSelectedPerson(this.personToEdit);
        return;
      }

      if (this.startInAddMode) {
        this.isEditing = false;
        this.selectedPerson = null;
        this.quickSignInCode = '';
        // Lease / tenant flows usually want a new active tenant
        this.isTenant = 1;
        this.active = 1;
        this.showNewPersonDialog = true;
        return;
      }

      if (this.shouldOpenDirectAdd()) {
        await this.handleDirectAddRoute();
        return;
      }

      // Check if an initial query exists, and if so, trigger the search
      if (this.tenantSearchQuery) {
        this.debouncedTenantSearch(); // Or call the appropriate search method
      }
    } finally {
      this.$nextTick(() => this.setupDialogDrag());
    }
  },

  beforeUnmount() {
    this._smsMediaGen = (this._smsMediaGen || 0) + 1;
    revokeSmsMediaUrls(this.smsMediaUrls);
    window.removeEventListener('keydown', this.onSmsDetailKeydown);
    this.teardownDialogDrag();
  },

  created() {
    document.title = 'New Contact';
    this.debouncedTenantSearch = this.debounce(this.onSearchTenantQuery, 300);
    this.applyLeasePropContext();
  },

  computed: {
    canManageQuickSignIn() {
      return !!(this.selectedPerson && this.selectedPerson.id);
    },
    filteredSmsHistory() {
      return filterSmsHistory(this.smsHistory, this.smsHistoryQuery);
    },
    quickSignInCodeChars() {
      const code = (this.quickSignInCode || '').toUpperCase();
      return Array.from({ length: 8 }, (_, i) => code[i] || '');
    },
    tenantCheckboxValue: {
      get() {
        return this.isTenant === 1; // Convert 1 to true for checkbox
      },
      set(newValue) {
        this.isTenant = newValue ? 1 : 0; // Convert true/false back to 1/0
      },
    },
    contractorCheckboxValue: {
      get() {
        return this.isContractor === 1;
      },
      set(newValue) {
        this.isContractor = newValue ? 1 : 0;
      },
    },
    activeCheckboxValue: {
      get() {
        return this.active === 1; // Convert 1 to true for checkbox
      },
      set(newValue) {
        this.active = newValue ? 1 : 0; // Convert true/false back to 1/0
      },
    },
  },

  methods: {
    toggleActive,
    canCheckPrimary,
    rowAfterDelete,
    addEmailRow,

    applyLeasePropContext() {
      if (this.leaseId == null) return;
      this.primaryLeaseId = this.leaseId;
      this.leaseCurrent = !!this.$props.leaseCurrent;
      this.primaryBlocked = !!this.$props.primaryBlocked;
      this.blockedBecause = this.leaseCurrent ? null : 'not-current';
    },

    primaryCheckboxTitle(row) {
      const enabled = this.canCheckPrimary({
        isActive: row.isActive,
        primaryBlocked: this.primaryBlocked,
        leaseCurrent: this.leaseCurrent,
      });
      if (enabled) return 'Primary';
      if (this.blockedBecause === 'multiple-current-leases') {
        return 'This person is on more than one current lease. Set primary from Lease Search.';
      }
      if (this.blockedBecause === 'not-current' || !this.leaseCurrent) {
        return 'Primary applies to a current lease.';
      }
      if (this.primaryBlocked || row.isActive) {
        return 'Another tenant on this lease is primary. Uncheck that tenant first.';
      }
      return 'Primary';
    },

    normalizeEmailRowList(raw) {
      if (!Array.isArray(raw)) return null;
      return raw.map((row) => ({
        id: row && row.id != null ? row.id : null,
        contactValue:
          row && row.contactValue != null ? String(row.contactValue) : '',
        isActive: !!(
          row &&
          (Number(row.isActive) === 1 || row.isActive === true)
        ),
      }));
    },

    emailRowsFromPerson(person) {
      let raw = person ? person.emailRows : null;
      if (typeof raw === 'string') {
        try {
          raw = JSON.parse(raw);
        } catch (error) {
          raw = null;
        }
      }
      const parsed = this.normalizeEmailRowList(raw);
      if (parsed) return parsed;
      return String((person && person.email) || '')
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
        .map((contactValue, index) => ({
          id: null,
          contactValue,
          isActive: index === 0,
        }));
    },

    applyPrimaryLease(primaryLease) {
      if (!primaryLease) {
        this.leaseCurrent = false;
        this.primaryBlocked = false;
        this.blockedBecause = null;
        this.isPrimaryTenant = false;
        this.primaryLeaseId = null;
        return;
      }
      if (primaryLease.blockedBecause === 'multiple-current-leases') {
        this.leaseCurrent = false;
        this.primaryBlocked = true;
        this.blockedBecause = 'multiple-current-leases';
        this.isPrimaryTenant = false;
        this.primaryLeaseId = null;
        return;
      }
      this.leaseCurrent = !!primaryLease.current;
      this.primaryBlocked = !!primaryLease.primaryBlocked;
      this.blockedBecause = primaryLease.blockedBecause || null;
      this.isPrimaryTenant = Number(primaryLease.isPrimaryTenant) === 1;
      this.primaryLeaseId = primaryLease.leaseId ?? null;
    },

    async loadEmailContacts(personId) {
      if (this.leaseId != null || !personId) return;
      try {
        const response = await axios.get(
          `/api/people/${personId}/email-contacts`,
        );
        if (this.leaseId != null) return;
        if (!this.selectedPerson || this.selectedPerson.id !== personId) return;
        const data = response.data || {};
        if (Array.isArray(data.emailRows)) {
          this.emailRows = this.normalizeEmailRowList(data.emailRows);
        }
        this.applyPrimaryLease(data.primaryLease || null);
      } catch (error) {
        console.error('Error loading email contacts:', error);
      }
    },

    initializeFromRoute() {
      const routeSearch = (this.$route.query.search || '').trim();
      const routeEmail = (this.$route.query.email || '').trim();

      if (routeSearch) {
        this.tenantSearchQuery = routeSearch;
      }

      if (routeEmail) {
        this.emailRows = [
          { id: null, contactValue: routeEmail, isActive: true },
        ];
      }
    },

    shouldOpenDirectAdd() {
      return this.$route.query.directAdd === '1';
    },

    normalizeEmail(email) {
      return (email || '').trim().toLowerCase();
    },

    personHasExactEmail(person, email) {
      const targetEmail = this.normalizeEmail(email);
      return (person.email || '')
        .split(',')
        .map((value) => this.normalizeEmail(value))
        .some((value) => value === targetEmail);
    },

    async handleDirectAddRoute() {
      const routeEmail = (this.$route.query.email || '').trim();

      if (!routeEmail) {
        this.showNewPersonDialog = true;
        return;
      }

      try {
        const response = await axios.get('/api/search-people', {
          params: {
            query: routeEmail,
          },
        });

        this.tenantSearchResults = response.data;

        const exactMatch = this.tenantSearchResults.find((person) =>
          this.personHasExactEmail(person, routeEmail),
        );

        if (exactMatch) {
          this.editSelectedPerson(exactMatch);
          return;
        }
      } catch (error) {
        console.error('Error looking up existing person by email:', error);
      }

      this.showNewPersonDialog = true;
    },

    updateFullName() {
      this.fullName = `${this.firstName} ${this.lastName}`.trim();
    },

    debounce(func, delay) {
      let timeout;
      return function (...args) {
        clearTimeout(timeout); // Clear the previous timeout
        timeout = setTimeout(() => {
          func.apply(this, args); // Apply the function after the delay
        }, delay);
      }.bind(this);
    },

    showFooterMessage(message) {
      // Set the footer message
      this.footerMessage = message;

      // Apply a class to set the background color
      this.footerMessageClass = 'highlight-footer-message';

      // Set a timeout to fade out the background color after 3 seconds
      setTimeout(() => {
        this.footerMessageClass = ''; // Remove the class to fade out
      }, 3000); // 3000 milliseconds = 3 seconds
    },

    editSelectedPerson(person) {
      this.showFooterMessage('Editing selected Person');
      this.selectedPerson = person;
      this.isEditing = true;
      this.showNewPersonDialog = true;
      this.populateForm(person);
      if (this.leaseId == null && person && person.id) {
        this.loadEmailContacts(person.id);
      }
      this.loadSms();
      // Blank quick code: same as pressing "Generate New" so the field is filled immediately
      if (!String(this.quickSignInCode || '').trim()) {
        this.generateNewCode({ silent: false, reason: 'auto-blank' });
      }
    },

    generateNewCode(options = {}) {
      if (!this.selectedPerson || !this.selectedPerson.id) {
        return Promise.resolve();
      }
      return axios
        .post(`/api/generate-quick-code/${this.selectedPerson.id}`)
        .then((response) => {
          this.quickSignInCode = response.data.quickSignInCode;
          // Keep selectedPerson in sync so list/UI reflect the new code after save/cancel
          if (this.selectedPerson) {
            this.selectedPerson.quickSignInCode = this.quickSignInCode;
          }
          const msg =
            options.reason === 'auto-blank'
              ? 'Quick sign-in code auto-generated (was blank)'
              : 'New quick sign-in code generated';
          if (options.silent !== true) {
            this.showFooterMessage(msg);
          }
        })
        .catch((error) => {
          console.error('Error generating code:', error);
          this.showFooterMessage(
            error.response?.data?.error || 'Error generating quick sign-in code',
          );
        });
    },

    async blacklistTenantTokens() {
      if (!this.selectedPerson || !this.selectedPerson.id) return;
      const name =
        this.selectedPerson.fullName ||
        `${this.firstName || ''} ${this.lastName || ''}`.trim() ||
        'this person';
      const confirmed = window.confirm(
        `Blacklist PixieChatBot tokens for ${name}?\n\n` +
          'They will be logged out of PixieChatBot on all devices and must sign in again.',
      );
      if (!confirmed) return;

      try {
        const response = await axios.post(
          `/api/blacklist-tenant-tokens/${this.selectedPerson.id}`,
        );
        const count = response.data?.revokedCount ?? 0;
        this.showFooterMessage(
          count === 0
            ? 'No active PixieChatBot sessions to blacklist'
            : `Blacklisted ${count} PixieChatBot session token${count === 1 ? '' : 's'}`,
        );
      } catch (error) {
        console.error('Error blacklisting tenant tokens:', error);
        this.showFooterMessage(
          error.response?.data?.error ||
            'Failed to blacklist PixieChatBot tokens',
        );
      }
    },

    onSmsDetailKeydown(event) {
      if (event.key !== 'Escape' || !this.smsDetail) return;
      event.preventDefault();
      this.closeSmsDetail();
    },
    openSmsDetail(row) {
      this.smsDetail = row;
    },
    closeSmsDetail() {
      this.smsDetail = null;
    },
    formatSmsPhone(digits) {
      const d = String(digits || "").replace(/\D/g, "");
      if (d.length !== 10) return digits || "";
      return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
    },
    formatSmsStamp(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      const iso = date.toISOString();
      return `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(2, 4)} ${iso.slice(11, 16)}`;
    },
    smsHistoryLine,
    async refreshSmsMedia(rows) {
      const generation = (this._smsMediaGen = (this._smsMediaGen || 0) + 1);
      const { urls, revoked } = await loadSmsMediaObjectUrls(
        rows,
        this.smsMediaUrls,
        {
          fetchBlob: (id) =>
            axios
              .get(`/api/sms/media/${id}`, { responseType: 'blob' })
              .then((res) => res.data),
          createObjectURL: (blob) => URL.createObjectURL(blob),
        },
      );
      if (this._smsMediaGen !== generation) {
        const live = new Set(Object.values(this.smsMediaUrls || {}));
        revokeSmsMediaUrls(
          Object.fromEntries(
            Object.entries(urls).filter(([, url]) => !live.has(url)),
          ),
        );
        return;
      }
      revokeSmsMediaUrls(Object.fromEntries(revoked.map((url) => [url, url])));
      this.smsMediaUrls = urls;
    },
    async loadSms() {
      if (!this.selectedPerson || !this.selectedPerson.id) return;
      this.smsMessage = "";
      try {
        const [statusRes, histRes] = await Promise.all([
          axios.get(`/api/people/${this.selectedPerson.id}/sms`),
          axios.get(`/api/people/${this.selectedPerson.id}/sms/messages`),
        ]);
        this.smsStatus = statusRes.data || this.smsStatus;
        this.smsHistory = histRes.data || [];
        this.refreshSmsMedia(this.smsHistory);
      } catch (error) {
        console.error("Error loading SMS status:", error);
        this.smsMessage =
          error.response?.data?.error || "Could not load SMS status";
      }
    },
    async setSmsOptIn(optIn) {
      if (!this.selectedPerson || !this.selectedPerson.id) return;
      if (optIn && !this.smsConfirmed) {
        this.smsMessage = "Check the confirmation box first.";
        return;
      }
      this.smsBusy = true;
      this.smsMessage = "";
      try {
        await axios.post(`/api/people/${this.selectedPerson.id}/sms`, {
          optIn,
          confirmed: this.smsConfirmed,
          phoneDigits: this.phoneInput,
        });
        this.smsConfirmed = false;
        await this.loadSms();
        this.smsMessage = optIn ? "SMS opt-in saved." : "SMS opt-out saved.";
      } catch (error) {
        this.smsMessage =
          error.response?.data?.error || "Failed to update SMS opt-in";
      } finally {
        this.smsBusy = false;
      }
    },
    async sendTestSms() {
      if (!this.selectedPerson || !this.selectedPerson.id) return;
      this.smsBusy = true;
      this.smsMessage = "";
      try {
        await axios.post(`/api/people/${this.selectedPerson.id}/sms/test`);
        await this.loadSms();
        this.smsMessage = "Test SMS sent.";
      } catch (error) {
        this.smsMessage =
          error.response?.data?.error || "Failed to send test SMS";
      } finally {
        this.smsBusy = false;
      }
    },
    populateForm(person) {
      this.firstName = person.First;
      this.lastName = person.Last;
      this.fullName = person.fullName;
      this.phoneInput = person.phone ? person.phone : '';
      this.emailRows = this.emailRowsFromPerson(person);
      this.isPrimaryTenant = Number(person.isPrimaryTenant) === 1;
      if (this.leaseId != null) {
        this.applyLeasePropContext();
      } else {
        this.leaseCurrent = false;
        this.primaryBlocked = false;
        this.blockedBecause = null;
        this.primaryLeaseId = null;
      }
      this.active = person.active;
      this.isTenant = person.isTenant;
      this.isContractor = person.isContractor;
      this.activeFrom = person.activeFrom;
      this.deactivatedOn = person.deactivatedOn;
      this.forwardingNote = person.forwardingNote;
      this.flags = person.flags;
      this.quickSignInCode = person.quickSignInCode || '';
    },

    submitForm() {
      // Normalize phones: 10-digit values become (NNN) NNN-NNNN; only block if invalid
      const phoneSegments = this.phoneInput
        .split(',')
        .map((phone) => phone.trim())
        .filter((phone) => phone);
      const phones = [];
      for (const segment of phoneSegments) {
        const formatted = this.formatCanonicalPhone(segment);
        if (!formatted) {
          this.newPersonErrMsg =
            'Invalid phone. Use (999) 555-1212 or 10 digits.';
          return;
        }
        phones.push(formatted);
      }
      this.phoneInput = phones.join(', ');

      let emails = this.emailRows
        .map((row) => ({
          id: row.id ?? null,
          contactValue: String(row.contactValue || '').trim(),
          isActive: row.isActive ? 1 : 0,
        }))
        .filter((row) => row.contactValue);

      if (emails.length && !emails.some((row) => row.isActive)) {
        emails = emails.map((row, index) =>
          index === 0 ? { ...row, isActive: 1 } : row,
        );
      }

      this.validatePhoneEmailInput(this.phoneInput, 'phone');
      if (this.newPersonErrMsg) {
        return;
      }
      const emailPattern =
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (emails.some((row) => !emailPattern.test(row.contactValue))) {
        this.newPersonErrMsg = 'expected format: someone@somewhere.com';
        return;
      }

      console.log('this.activeFrom:', this.activeFrom);

      if (!this.activeFrom) {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
        const day = String(today.getDate()).padStart(2, '0');
        this.activeFrom = `${year}-${month}-${day}`;
      }

      const newPerson = {
        firstName: this.firstName,
        lastName: this.lastName,
        fullName: this.fullName,
        phones: phones,
        emails: emails,
        isTenant: this.isTenant ? 1 : 0, // New field
        isContractor: this.isContractor ? 1 : 0,
        active: this.active ? 1 : 0, // New field
        activeFrom: this.activeFrom || null, // New field
        deactivatedOn: this.deactivatedOn || null, // New field
        forwardingNote: this.forwardingNote, // New field
        flags: this.flags, // New field
      };
      if (this.leaseCurrent && this.primaryLeaseId != null) {
        newPerson.leaseId = this.primaryLeaseId;
        newPerson.isPrimaryTenant = this.isPrimaryTenant ? 1 : 0;
      }
      console.log('newPerson:', newPerson);

      if (this.isEditing) {
        this.editPerson(newPerson);
      } else {
        this.addPerson(newPerson);
      }
    },

    async addPerson(newPerson) {
      try {
        const response = await axios.post('/api/add-person', newPerson);
        const peopleId = response.data.peopleId;
        // Stay on the same full person form (now as edit) with Quick Sign-In available
        this.selectedPerson = {
          id: peopleId,
          fullName: newPerson.fullName,
          First: newPerson.firstName,
          Last: newPerson.lastName,
          phone: (newPerson.phones || []).join(', '),
          email: (newPerson.emails || [])
            .map((row) => row.contactValue)
            .filter(Boolean)
            .join(', '),
          emailRows: newPerson.emails || [],
          isPrimaryTenant: this.isPrimaryTenant ? 1 : 0,
          isTenant: newPerson.isTenant,
          isContractor: newPerson.isContractor,
          active: newPerson.active,
          activeFrom: newPerson.activeFrom,
          deactivatedOn: newPerson.deactivatedOn,
          forwardingNote: newPerson.forwardingNote,
          flags: newPerson.flags,
        };
        this.isEditing = true;
        await this.generateNewCode({ silent: false, reason: 'auto-blank' });
        this.showFooterMessage(
          'Person saved. Quick Sign-In code is ready; use Save or close when done.',
        );
        // Parent-opened add (e.g. LeaseSearch): stay open so user can copy the code
        if (!this.startInAddMode && !this.personToEdit) {
          this.refreshSearchResults();
        }
      } catch (error) {
        console.error('Error adding person:', error);
        this.showFooterMessage(
          error.response?.data?.error ||
            'An error occurred while adding the person.',
        );
      }
    },

    async editPerson(newPerson) {
      const personId = this.selectedPerson.id;
      try {
        // If Quick Sign-In Code is blank, generate one before saving
        if (!String(this.quickSignInCode || '').trim()) {
          const codeResponse = await axios.post(
            `/api/generate-quick-code/${personId}`,
          );
          this.quickSignInCode = codeResponse.data.quickSignInCode || '';
          this.showFooterMessage(
            'Quick sign-in code auto-generated for this person',
          );
        }

        await axios.put(`/api/edit-person/${personId}`, newPerson);
        this.resetForm();
        this.showNewPersonDialog = false;
        this.selectedPerson = null;
        // Opened from a parent for add/edit: close the whole dialog after save
        if (this.personToEdit || this.startInAddMode) {
          this.closeTenantSearchDialog();
          return;
        }
        this.refreshSearchResults();
      } catch (error) {
        console.error('Error editing person:', error);
        this.showFooterMessage(
          error.response?.data?.error ||
            'Error saving person. Please try again.',
        );
      }
    },

    cancelAddPerson() {
      this.resetForm();
      this.showNewPersonDialog = false;
      this.selectedPerson = null;
      // Opened directly for add/edit from a parent: cancel closes the dialog
      if (this.personToEdit || this.startInAddMode) {
        this.closeTenantSearchDialog();
      }
    },

    resetForm() {
      this.firstName = '';
      this.lastName = '';
      this.fullName = '';
      this.phoneInput = '';
      this.emailRows = [];
      this.isPrimaryTenant = false;
      this.primaryBlocked = false;
      this.leaseCurrent = false;
      this.blockedBecause = null;
      this.primaryLeaseId = null;
      this.applyLeasePropContext();
      this.newPersonErrMsg = '';
    },

    refreshSearchResults() {
      this.onSearchTenantQuery();
    },

    onSearchTenantQuery() {
      if (this.tenantSearchQuery.trim() === '') {
        this.tenantSearchResults = [];
        return;
      }

      axios
        .get('/api/search-people', {
          params: {
            query: this.tenantSearchQuery,
          },
        })
        .then((response) => {
          this.tenantSearchResults = response.data;
        })
        .catch((error) => {
          console.error('Error fetching tenants:', error);
        });
    },

    handleClick() {
      if (this.clickTimeout) {
        clearTimeout(this.clickTimeout);
        this.clickTimeout = null;
      }

      this.clickTimeout = setTimeout(() => {
        // some action on click
        this.clickTimeout = null;
      }, 250);
    },

    handleDblClick(person) {
      if (this.clickTimeout) {
        clearTimeout(this.clickTimeout);
        this.clickTimeout = null;
      }

      this.editSelectedPerson(person);
    },

    highlightTerm(queryInput, text) {
      if (queryInput) {
        const tokens = queryInput.split(',').map((token) => token.trim());
        const query =
          tokens.length === 1 ? tokens[0] : tokens[tokens.length - 1];
        const escapedQuery = query.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
        const regex = new RegExp(`(${escapedQuery})`, 'gi');

        if (text) {
          return text.replace(regex, '<span class="highlight">$1</span>');
        }
      }
      return text;
    },

    toggleNewPersonDialog() {
      this.showNewPersonDialog = !this.showNewPersonDialog;
      this.isEditing = !this.showNewPersonDialog;
    },

    extractPhoneDigits(phone) {
      return String(phone || '').replace(/\D/g, '');
    },

    /**
     * Return canonical (NNN) NNN-NNNN if the value has 10 usable digits
     * (or 11 with a leading 1). Otherwise null.
     */
    formatCanonicalPhone(phone) {
      let digits = this.extractPhoneDigits(phone);
      if (digits.length === 11 && digits.startsWith('1')) {
        digits = digits.slice(1);
      }
      if (digits.length !== 10) {
        return null;
      }
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    },

    validatePhoneEmailInput(input, type) {
      if (type === 'phone') {
        if (!input || !String(input).trim()) {
          this.newPersonErrMsg = '';
          return;
        }
        const segments = String(input)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        const allValid = segments.every(
          (segment) => this.formatCanonicalPhone(segment) !== null,
        );
        // Allow non-canonical 10-digit values; they are reformatted on save
        this.newPersonErrMsg = allValid
          ? ''
          : 'Invalid phone. Use (999) 555-1212 or 10 digits.';
      }
    },

    closeTenantSearchDialog() {
      this.tenantSearchQuery = '';
      this.showNewPersonDialog = false;
      this.teardownDialogDrag();
      this.$emit('closeDialog');
    },

    setupDialogDrag() {
      const el = this.$refs.personDialog;
      if (!el || !(el instanceof HTMLElement)) return;

      this.teardownDialogDrag();

      // Restore last position if available; otherwise keep CSS default centering
      const savedTop = localStorage.getItem('newPersonDialogTop');
      const savedLeft = localStorage.getItem('newPersonDialogLeft');
      if (savedTop && savedLeft) {
        el.style.transform = 'none';
        el.style.top = savedTop;
        el.style.left = savedLeft;
      }

      let pos1 = 0;
      let pos2 = 0;
      let pos3 = 0;
      let pos4 = 0;
      const header = el.querySelector('.draggable-header');
      const handle = header || el;

      const dragMouseDown = (e) => {
        if (e.button !== 0) return;
        // Don't start a drag from the close control
        if (e.target && e.target.closest && e.target.closest('.close-x-btn')) {
          return;
        }
        e.preventDefault();

        // Convert transform-based layout to absolute top/left for dragging
        const rect = el.getBoundingClientRect();
        el.style.transform = 'none';
        el.style.top = `${rect.top}px`;
        el.style.left = `${rect.left}px`;

        pos3 = e.clientX;
        pos4 = e.clientY;
        document.addEventListener('mousemove', elementDrag);
        document.addEventListener('mouseup', closeDragElement);
      };

      const elementDrag = (e) => {
        e.preventDefault();
        pos1 = pos3 - e.clientX;
        pos2 = pos4 - e.clientY;
        pos3 = e.clientX;
        pos4 = e.clientY;
        el.style.top = `${el.offsetTop - pos2}px`;
        el.style.left = `${el.offsetLeft - pos1}px`;
      };

      const closeDragElement = () => {
        document.removeEventListener('mousemove', elementDrag);
        document.removeEventListener('mouseup', closeDragElement);
        // Keep the dialog roughly on screen
        const rect = el.getBoundingClientRect();
        const minVisible = 40;
        let top = rect.top;
        let left = rect.left;
        if (rect.right < minVisible) left = minVisible - rect.width;
        if (rect.bottom < minVisible) top = minVisible - rect.height;
        if (rect.left > window.innerWidth - minVisible) {
          left = window.innerWidth - minVisible;
        }
        if (rect.top > window.innerHeight - minVisible) {
          top = window.innerHeight - minVisible;
        }
        if (top < 0) top = 0;
        if (left < 0) left = 0;
        el.style.top = `${top}px`;
        el.style.left = `${left}px`;
        localStorage.setItem('newPersonDialogTop', el.style.top);
        localStorage.setItem('newPersonDialogLeft', el.style.left);
      };

      handle.addEventListener('mousedown', dragMouseDown);
      this._dialogDragCleanup = () => {
        handle.removeEventListener('mousedown', dragMouseDown);
        document.removeEventListener('mousemove', elementDrag);
        document.removeEventListener('mouseup', closeDragElement);
        this._dialogDragCleanup = null;
      };
    },

    teardownDialogDrag() {
      if (typeof this._dialogDragCleanup === 'function') {
        this._dialogDragCleanup();
      }
    },
  },

  watch: {
    firstName() {
      this.updateFullName();
    },
    lastName() {
      this.updateFullName();
    },

    fullName() {
      if (this.fullName == 'Unknown Unknown') {
        this.fullName = '';
      }
    },

    phoneInput(newVal) {
      this.validatePhoneEmailInput(newVal, 'phone');
    },
    showNewPersonDialog(NewVal) {
      if (NewVal) {
        this.$nextTick(() => {
          const firstNameInput = this.$refs.firstName;
          if (!firstNameInput) {
            return;
          }
          firstNameInput.focus();
          // If default "Unknown", select all so typing replaces it immediately
          if (
            String(this.firstName || '').trim().toLowerCase() === 'unknown'
          ) {
            firstNameInput.select();
          }
        });
      }
    },
  },
};
</script>

<style scoped>
/* tenantt search dialog section */
.edit-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(63, 63, 63, 0.5);
  z-index: 1000;
}

.edit-dialog {
  position: fixed;
  top: 50%;
  left: 200px;
  transform: translate(-5%, -50%);
  background-color: #c9c9c9;
  border-radius: 8px;
  box-shadow: 8px 7px 10px 6px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  width: 650px;
  padding: 10px;
  font-size: 0.8em;
}

.draggable-header {
  cursor: move;
  user-select: none;
}

.edit-dialog-content h2 {
  user-select: none;
  margin-top: -15px;
  font-size: 20px;
  text-align: center;
  background: #3b3b3b;
  height: 33px;
  border-radius: 5px 5px 0 0;
  padding-top: 10px;
  margin-left: -15px;
  margin-right: -15px;
  color: #ddd;
  font-size: 20px;
  text-align: center;
}

.edit-dialog-body {
  margin: 20px 0;
  width: 95%;
}

.edit-dialog-body label {
  display: block;
  font-weight: bold;
  margin-bottom: 5px;
}

.edit-dialog-body input[type='text'],
.edit-dialog-body input[type='number'] {
  width: 100%; /* Adjusted width to be consistent for all inputs */
  padding: 6px;
  margin-bottom: 5px;
  border: 1px solid #9d9d9d;
  border-radius: 4px;
}

.edit-dialog-body input[type='date'] {
  width: 107px !important;
  border-radius: 4px;
  border: 1pt solid #888;
}

.edit-dialog-body .search-input {
  width: 170px !important;
  margin-left: 12px;
}

.edit-dialog-body ul {
  margin-left: -36px;
  margin-top: 2px;
  list-style-type: none;
}

.edit-dialog-body li {
  padding: 3px;
}

.edit-dialog-body li:nth-child(even) {
  background-color: #eee;
}

.edit-dialog-body li:nth-child(odd) {
  background-color: #ddd;
}

.sms-panel {
  width: 100%;
  box-sizing: border-box;
}

.sms-panel .sms-status {
  font-size: 0.95em;
  margin-bottom: 6px;
}

.sms-panel .sms-confirm {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
  font-weight: normal;
  font-size: 0.85em;
  line-height: 1.3;
  margin: 6px 0;
  gap: 8px;
}

.sms-panel .sms-confirm input {
  width: auto !important;
  flex: 0 0 auto;
  margin: 0.15em 0 0;
}

.sms-panel .sms-confirm span {
  flex: 1;
  text-align: left;
}

.sms-panel .sms-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 6px 0;
}

.sms-panel .btn-small {
  padding: 4px 8px;
  font-size: 0.85em;
}

.sms-panel .sms-note {
  font-size: 0.85em;
  color: #444;
}

.sms-history-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 6px;
}

.sms-panel .sms-history-filter {
  flex: 1 1 auto;
  width: auto !important;
  min-width: 0;
  box-sizing: border-box;
  margin: 0;
}

.sms-panel .sms-history-refresh {
  flex: 0 0 auto;
  width: auto !important;
}

.sms-history-scroll {
  max-height: 160px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.sms-history-scroll::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.sms-panel .sms-history {
  width: 100%;
  border-collapse: collapse;
  margin: 0;
  font-size: 0.8em;
  text-align: left;
}

.sms-panel .sms-history th,
.sms-panel .sms-history td {
  text-align: left;
  padding: 4px 6px;
  border-bottom: 1px solid #ddd;
  vertical-align: top;
}

.sms-panel .sms-history-row {
  cursor: pointer;
}

.sms-panel .sms-history-row:hover {
  background: #e7e0ea;
}

.sms-detail-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sms-detail-popup {
  position: relative;
  width: min(520px, 92vw);
  max-height: 80vh;
  overflow: auto;
  background: #fff;
  color: #222;
  border-radius: 8px;
  padding: 28px 16px 16px;
  text-align: left;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
}

.sms-detail-popup h3 {
  margin: 0 0 10px;
  text-align: left;
}

.sms-detail-close {
  position: absolute;
  top: 4px;
  right: 8px;
  width: auto !important;
  padding: 0 6px !important;
  background: none !important;
  color: #333 !important;
  font-size: 1.6rem;
  line-height: 1;
  cursor: pointer;
}

.sms-detail-fields {
  display: grid;
  grid-template-columns: 90px 1fr;
  gap: 4px 8px;
  margin: 0 0 10px;
}

.sms-detail-fields dt {
  font-weight: 600;
}

.sms-detail-fields dd {
  margin: 0;
}

.sms-detail-body {
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  font-family: inherit;
}

.sms-detail-media {
  display: block;
  width: 100%;
  height: auto;
  margin-top: 8px;
  object-fit: contain;
}

.edit-dialog-body li:hover {
  background-color: #907793;
}

.edit-dialog-footer,
.dialog-buttons {
  text-align: center;
}

.edit-dialog-footer button,
.dialog-buttons button {
  background-color: #a080a0;
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin: 0 5px;
}

.edit-dialog-footer button:hover,
.dialog-buttons button:hover {
  background-color: #846984;
}

.dialog-buttons {
  background-color: #dc3545;
}

.note-textarea {
  width: calc(100% - 20px);
  height: 200px;
  margin-bottom: 15px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: lightyellow;
}

.tenant-search-dialog {
}

.tenant-search-dialog .edit-dialog {
  max-height: 780px;
}

.tenant-search-dialog .search-results {
  height: 320px;
  border: 1pt solid #eee;
  padding: 3px;
  width: 98%;
  overflow-y: auto;
  border-radius: 4px;
  margin-left: 12px;
  margin-top: 5px;
  font-size: 10pt;
}

.tenant-search-dialog .edit-dialog-content {
  height: 95%;
  padding: 5px;
  border: 1 pt solid #eee;
}

.tenant-search-dialog .edit-dialog-footer {
  margin-top: 15px;
}

.tenant-search-dialog .edit-dialog-footer button {
  width: 140px;
}
.scrollbar-wrapper {
  /* Tall enough for the full person form (incl. Quick Sign-In) without a bar */
  height: 560px;
  max-height: min(560px, calc(100vh - 160px));
  overflow-y: auto;
  overflow-x: hidden;
  border: 1pt solid #ddd;
  padding: 10px;
  width: 98%;
  margin-top: 3px;
  margin-left: 3px;
  border-radius: 4px;
  /* Keep scroll if content grows; hide the scrollbar chrome */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* legacy Edge */
}
.scrollbar-wrapper::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none; /* Chrome, Safari, Opera */
}

.form-row {
  display: flex;
  flex-wrap: wrap;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.4rem;
}

.quick-code-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.quick-code-boxes {
  display: flex;
}

.quick-code-box {
  width: 28px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: monospace;
  font-size: 1.6em;
  font-weight: 600;
  border: 2px solid #ccc;
  border-radius: 6px;
  background: #f9f9f9;
  color: #333;
  text-transform: uppercase;
}

.quick-code-box.filled {
  border-color: #4a90d9;
  background: #eef4fb;
}

.generate-code-btn {
  padding: 6px 16px;
  font-size: 0.85em;
  white-space: nowrap;
  cursor: pointer;
  background: #4a90d9;
  color: white;
  border: none;
  border-radius: 4px;
}

.generate-code-btn:hover:not(:disabled) {
  background: #357abd;
}

.blacklist-token-btn {
  padding: 6px 16px;
  font-size: 0.85em;
  white-space: nowrap;
  cursor: pointer;
  background: #c0392b;
  color: white;
  border: none;
  border-radius: 4px;
}

.blacklist-token-btn:hover:not(:disabled) {
  background: #a93226;
}

.generate-code-btn:disabled,
.blacklist-token-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quick-code-hint {
  margin-top: 6px;
  font-size: 0.85em;
  color: #555;
}

.half-width {
  width: 50%;
}

.full-width {
  width: 100%;
}
.quarter-width {
  width: 50%;
  text-align: center;
}

.button-row {
  margin-top: 0.5rem;
}

.checkbox-row {
  display: flex;
  gap: 20px;
  margin-bottom: 0.4rem;
}

.checkbox-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 70px;
}

.add-new-person input {
  padding: 0.3rem;
  margin-bottom: 0.2rem;
  width: 87% !important;
}

.email-rows {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.email-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.email-flag {
  flex: 0 0 18px;
  width: 18px;
  text-align: center;
  font-weight: 600;
}

.email-rows input[type='checkbox'] {
  flex: 0 0 18px;
  width: auto !important;
  margin: 0;
  padding: 0;
}

.email-rows input[type='text'] {
  flex: 1;
  width: auto !important;
  min-width: 0;
}

.email-row-add {
  justify-content: flex-end;
}

.add-new-person .wide-input {
  width: 90%;
}

.add-new-person .tall-input {
  height: 40px;
}

.button-group {
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
}

.button-group button {
  width: 48%;
}

.add-new-person button {
  display: block;
  padding: 12px;
  background-color: #a080a0;
  color: white;
  border: none;
  cursor: pointer;
  width: 100px;
}

.add-new-person .email-rows button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto !important;
  flex: 0 0 auto;
  padding: 4px 8px;
}

.add-new-person .email-rows .email-trash {
  background: transparent;
  color: #333;
  padding: 2px 6px;
}

.add-new-person textarea {
  width: 255px;
  height: 40px;
  font-size: 1.1em;
}

#forwardingNote {
  height: 120px;
}

.add-new-person .button-row button {
  padding: 5px;
  height: 38px;
  border-radius: 4px;
  background-color: #a080a0;
  border: 1pt solid #888;
}

.add-new-person .button-row {
  justify-content: flex-end;
  gap: 26px;
  text-align: center;
  margin-right: 21px;
}

.add-new-person .error-msg {
  border: 1pt solid #b3b3b3;
  color: #f14242;
  width: 563px;
  height: 17px;
  border-radius: 4px;
  padding: 5px;
}

.search-results {
  border: 1pt solid #eee;
  padding: 3px;
  width: 401px;
  overflow-y: auto;
  border-radius: 4px;
  margin-left: 12px;
  margin-top: 5px;
  font-size: 10pt;
}

.selected-item {
  background-color: #907793;
}

.dialog-title-bar {
  position: relative;
  width: 100%;
}

.close-x-btn {
  position: absolute;
  top: 3px;
  right: 0;
  background: none !important;
  border: none !important;
  font-size: 2rem;
  cursor: pointer;
  color: #f8f8f8;
  padding: 0 6px !important;
  line-height: 1;
  width: auto !important;
}

.close-x-btn:hover {
  color: #333;
}

.edit-dialog-footer {
  margin-top: 15px;
}

.edit-dialog-footer button {
  width: 140px;
}

.error-msg {
  color: red;
  margin-top: 10px;
}

.highlight {
  background: #d6bbda;
}

.add-new-person .wide-input {
  width: 100%;
}

.add-new-person .tall-input {
  height: 40px;
}

.left-column input {
  width: 300px;
}
</style>
